import React from 'react';
import { LoadingIcon } from '@assets/icon';
import type { DefaultCommand } from '../../../../types/chat-command';
import './OtherFeaturesFlyout.scss';

interface Props {
  open: boolean;
  commands: DefaultCommand[];
  loading: boolean;
  onSelect: (command: DefaultCommand) => void;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
}

const CommandIcon: React.FC<{ command: DefaultCommand }> = ({ command }) => {
  const [failed, setFailed] = React.useState(false);
  const source = command.imageUrl;
  React.useEffect(() => { setFailed(false); }, [source]);
  if (!source || failed) return null;
  return <img src={source} alt={command.name} onError={() => setFailed(true)} />;
};

const useAvailableHeight = (open: boolean) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = React.useState<number>();
  const update = React.useCallback(() => {
    const top = ref.current?.getBoundingClientRect().top;
    if (top === undefined) return;
    setMaxHeight(Math.max(80, window.innerHeight - top - 8));
  }, []);
  React.useLayoutEffect(() => {
    if (!open) return;
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [open, update]);
  return { ref, maxHeight };
};

const FlyoutContent: React.FC<Pick<Props, 'commands' | 'loading' | 'onSelect'>> = ({
  commands, loading, onSelect,
}) => {
  if (loading) return <div className="other-features-flyout__state" role="status"><LoadingIcon />Đang tải...</div>;
  if (!commands.length) return <div className="other-features-flyout__state">Chưa có tính năng</div>;
  return <>{commands.map((command) => (
    <button key={command.id} type="button" role="menuitem" onClick={() => onSelect(command)}>
      <span className="other-features-flyout__icon"><CommandIcon command={command} /></span>
      <span>{command.name}</span>
    </button>
  ))}</>;
};

const OtherFeaturesFlyout: React.FC<Props> = ({
  open, commands, loading, onSelect, onPointerEnter, onPointerLeave,
}) => {
  const { ref, maxHeight } = useAvailableHeight(open);
  if (!open) return null;
  return <div ref={ref} id="other-features-menu" className="other-features-flyout" role="menu"
    style={{ maxHeight }}
    aria-label="Tính năng khác" onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave}>
    <FlyoutContent commands={commands} loading={loading} onSelect={onSelect} />
  </div>;
};

export default OtherFeaturesFlyout;
